/**
 * Dropdown catalog of in-browser gesture actions.
 * Pure browser APIs only — no downloads, no desktop companion.
 */

import type { ActionThen } from "../engines/types";

export const DEFAULT_VIDEO_PROMPT =
  "Cinematic clip, {{gesture_label}} cue, magenta forge workshop, volumetric light, 24fps film";

export const DEFAULT_IMAGE_PROMPT =
  "Still of {{gesture_label}}, violet forge workshop, cinematic light, detailed, 35mm";

export type ActionPresetId =
  | "none"
  | "toast"
  | "beep"
  | "speak"
  | "copy_gesture"
  | "copy_frame"
  | "grab_to_ask"
  | "toggle_mappings"
  | "toggle_camera"
  | "flash_stage"
  | "scroll_down"
  | "scroll_up"
  | "history_back"
  | "fullscreen"
  | "open_url"
  | "forge_image"
  | "forge_video"
  | "forge_voice"
  | "hotkey"
  | "custom"
  | "custom_ref";

export type ActionPreset = {
  id: ActionPresetId;
  label: string;
  /** Short hint under the dropdown */
  hint?: string;
  needsUrl?: boolean;
  needsKeys?: boolean;
  needsText?: boolean;
  /** Resume can fire while mappings paused */
  fireWhenPaused?: boolean;
};

export const ACTION_PRESETS: ActionPreset[] = [
  { id: "none", label: "Do nothing" },
  { id: "toast", label: "Show toast", hint: "Banner — edit text below", needsText: true },
  { id: "beep", label: "Play beep", hint: "Web Audio confirmation" },
  { id: "speak", label: "Speak text", hint: "speechSynthesis — edit text", needsText: true },
  { id: "copy_gesture", label: "Copy text", hint: "Clipboard — edit text", needsText: true },
  { id: "copy_frame", label: "Copy camera frame", hint: "Clipboard image" },
  { id: "grab_to_ask", label: "Grab frame → Ask", hint: "Snapshot + switch to Ask" },
  {
    id: "toggle_mappings",
    label: "Pause / resume mappings",
    hint: "Works even while paused",
    fireWhenPaused: true,
  },
  { id: "toggle_camera", label: "Stop / start camera" },
  { id: "flash_stage", label: "Flash camera stage", hint: "Visual feedback" },
  { id: "scroll_down", label: "Scroll page down" },
  { id: "scroll_up", label: "Scroll page up" },
  { id: "history_back", label: "Browser back" },
  { id: "fullscreen", label: "Toggle fullscreen" },
  { id: "open_url", label: "Open URL…", needsUrl: true, hint: "New tab" },
  { id: "forge_image", label: "Open ImageForge", hint: "Opens studio and starts generate" },
  { id: "forge_video", label: "Open VideoForge", hint: "Opens studio and starts generate" },
  { id: "forge_voice", label: "Open VoiceForge", hint: "New tab" },
  { id: "hotkey", label: "Dispatch hotkey…", needsKeys: true, hint: "In-page KeyboardEvent only" },
  {
    id: "custom",
    label: "⚙ Build your own…",
    hint: "Multi-step custom output (toast + beep + URL…)",
  },
];

/** Step kinds available inside the custom builder (leaf actions only). */
export type StepKindId =
  | "toast"
  | "beep"
  | "speak"
  | "copy_text"
  | "scroll_down"
  | "scroll_up"
  | "open_url"
  | "hotkey"
  | "emit"
  | "fullscreen"
  | "history_back"
  | "copy_frame"
  | "grab_to_ask"
  | "toggle_mappings"
  | "toggle_camera"
  | "flash_stage"
  | "forge_image"
  | "forge_video"
  | "forge_voice";

export type StepKindMeta = {
  id: StepKindId;
  label: string;
  /** Param fields shown in builder */
  fields?: ("text" | "url" | "keys" | "channel" | "frequency" | "dy")[];
};

export const STEP_KINDS: StepKindMeta[] = [
  { id: "toast", label: "Toast message", fields: ["text"] },
  { id: "beep", label: "Beep", fields: ["frequency"] },
  { id: "speak", label: "Speak", fields: ["text"] },
  { id: "copy_text", label: "Copy text", fields: ["text"] },
  { id: "scroll_down", label: "Scroll down", fields: ["dy"] },
  { id: "scroll_up", label: "Scroll up", fields: ["dy"] },
  { id: "open_url", label: "Open URL", fields: ["url"] },
  { id: "hotkey", label: "Hotkey", fields: ["keys"] },
  { id: "emit", label: "Emit channel", fields: ["channel"] },
  { id: "fullscreen", label: "Toggle fullscreen" },
  { id: "history_back", label: "Browser back" },
  { id: "copy_frame", label: "Copy camera frame" },
  { id: "grab_to_ask", label: "Grab frame → Ask" },
  { id: "toggle_mappings", label: "Pause / resume mappings" },
  { id: "toggle_camera", label: "Stop / start camera" },
  { id: "flash_stage", label: "Flash stage" },
  { id: "forge_image", label: "Open ImageForge · generate", fields: ["text"] },
  { id: "forge_video", label: "Open VideoForge · generate", fields: ["text"] },
  { id: "forge_voice", label: "Open VoiceForge" },
];

export const TEMPLATE_HINT =
  "Templates: {{gesture}} {{gesture_label}} {{confidence}} {{time}}";

type StepFieldOpts = {
  text?: string;
  url?: string;
  keys?: string;
  channel?: string;
  frequency?: number;
  dy?: number;
  prompt?: string;
  autogen?: string;
  provider?: string;
  pick?: string;
  model?: string;
  sku?: string;
  rating?: string;
};

function handoffParams(
  product: "imageforge" | "videoforge",
  opts?: StepFieldOpts,
): Record<string, string> {
  const fallback = product === "videoforge" ? DEFAULT_VIDEO_PROMPT : DEFAULT_IMAGE_PROMPT;
  const params: Record<string, string> = {
    prompt: opts?.prompt || opts?.text || fallback,
    autogen: opts?.autogen === "0" ? "0" : "1",
    provider: opts?.provider || "any",
    pick: opts?.pick || "random",
    rating: opts?.rating || "green",
  };
  if (opts?.model) params.model = opts.model;
  if (opts?.sku) params.sku = opts.sku;
  return params;
}

export function stepKindToThen(id: StepKindId, opts?: StepFieldOpts): ActionThen {
  switch (id) {
    case "toast":
      return { kind: "toast", message: opts?.text ?? "{{gesture_label}}" };
    case "beep":
      return { kind: "beep", frequency: opts?.frequency ?? 880 };
    case "speak":
      return { kind: "speak", text: opts?.text ?? "{{gesture_label}}" };
    case "copy_text":
      return { kind: "copy_text", text: opts?.text ?? "{{gesture}}" };
    case "scroll_down":
      return { kind: "scroll", dy: opts?.dy && opts.dy > 0 ? opts.dy : 420 };
    case "scroll_up":
      return { kind: "scroll", dy: opts?.dy && opts.dy < 0 ? opts.dy : -(opts?.dy || 420) };
    case "open_url":
      return { kind: "open_url", url: opts?.url || "https://forgesuiteui.com" };
    case "hotkey":
      return { kind: "hotkey", keys: opts?.keys || "Enter" };
    case "emit":
      return { kind: "emit", channel: opts?.channel || "visionforge.custom" };
    case "fullscreen":
      return { kind: "fullscreen" };
    case "history_back":
      return { kind: "history", dir: "back" };
    case "copy_frame":
      return { kind: "app", command: "copy_frame" };
    case "grab_to_ask":
      return { kind: "app", command: "grab_to_ask" };
    case "toggle_mappings":
      return { kind: "app", command: "toggle_mappings" };
    case "toggle_camera":
      return { kind: "app", command: "toggle_camera" };
    case "flash_stage":
      return { kind: "app", command: "flash_stage" };
    case "forge_image":
      return { kind: "forge_handoff", product: "imageforge", params: handoffParams("imageforge", opts) };
    case "forge_video":
      return { kind: "forge_handoff", product: "videoforge", params: handoffParams("videoforge", opts) };
    case "forge_voice":
      return { kind: "forge_handoff", product: "voiceforge" };
    default:
      return { kind: "toast", message: "{{gesture_label}}" };
  }
}

export function thenToStepKind(then: ActionThen): StepKindId {
  switch (then.kind) {
    case "toast":
      return "toast";
    case "beep":
      return "beep";
    case "speak":
      return "speak";
    case "copy_text":
      return "copy_text";
    case "scroll":
      return then.dy >= 0 ? "scroll_down" : "scroll_up";
    case "open_url":
      return "open_url";
    case "hotkey":
      return "hotkey";
    case "emit":
      return "emit";
    case "fullscreen":
      return "fullscreen";
    case "history":
      return "history_back";
    case "forge_handoff":
      if (then.product === "videoforge") return "forge_video";
      if (then.product === "voiceforge") return "forge_voice";
      return "forge_image";
    case "app":
      if (then.command === "copy_frame") return "copy_frame";
      if (then.command === "grab_to_ask") return "grab_to_ask";
      if (then.command === "toggle_mappings") return "toggle_mappings";
      if (then.command === "toggle_camera") return "toggle_camera";
      if (then.command === "flash_stage") return "flash_stage";
      return "toast";
    default:
      return "toast";
  }
}

export function presetToThen(
  id: ActionPresetId,
  opts?: { url?: string; keys?: string; text?: string; outputId?: string },
): ActionThen {
  switch (id) {
    case "none":
      return { kind: "none" };
    case "toast":
      return { kind: "toast", message: opts?.text || "{{gesture_label}}" };
    case "beep":
      return { kind: "beep" };
    case "speak":
      return { kind: "speak", text: opts?.text || "{{gesture_label}}" };
    case "copy_gesture":
      return { kind: "copy_text", text: opts?.text || "{{gesture}}" };
    case "copy_frame":
      return { kind: "app", command: "copy_frame" };
    case "grab_to_ask":
      return { kind: "app", command: "grab_to_ask" };
    case "toggle_mappings":
      return { kind: "app", command: "toggle_mappings" };
    case "toggle_camera":
      return { kind: "app", command: "toggle_camera" };
    case "flash_stage":
      return { kind: "app", command: "flash_stage" };
    case "scroll_down":
      return { kind: "scroll", dy: 420 };
    case "scroll_up":
      return { kind: "scroll", dy: -420 };
    case "history_back":
      return { kind: "history", dir: "back" };
    case "fullscreen":
      return { kind: "fullscreen" };
    case "open_url":
      return { kind: "open_url", url: opts?.url || "https://forgesuiteui.com" };
    case "forge_image":
      return {
        kind: "forge_handoff",
        product: "imageforge",
        params: handoffParams("imageforge", { text: opts?.text }),
      };
    case "forge_video":
      return {
        kind: "forge_handoff",
        product: "videoforge",
        params: handoffParams("videoforge", { text: opts?.text }),
      };
    case "forge_voice":
      return { kind: "forge_handoff", product: "voiceforge" };
    case "hotkey":
      return { kind: "hotkey", keys: opts?.keys || "Enter" };
    case "custom":
      return {
        kind: "sequence",
        label: "Custom",
        steps: [{ kind: "toast", message: "{{gesture_label}} fired" }, { kind: "beep" }],
      };
    case "custom_ref":
      return { kind: "custom_ref", outputId: opts?.outputId || "" };
    default:
      return { kind: "none" };
  }
}

export function thenToPreset(then: ActionThen): ActionPresetId {
  switch (then.kind) {
    case "none":
      return "none";
    case "toast":
      return "toast";
    case "beep":
      return "beep";
    case "speak":
      return "speak";
    case "copy_text":
      return "copy_gesture";
    case "scroll":
      return then.dy >= 0 ? "scroll_down" : "scroll_up";
    case "history":
      return "history_back";
    case "fullscreen":
      return "fullscreen";
    case "open_url":
      return "open_url";
    case "forge_handoff":
      if (then.product === "videoforge") return "forge_video";
      if (then.product === "voiceforge") return "forge_voice";
      return "forge_image";
    case "hotkey":
      return "hotkey";
    case "app":
      if (then.command === "copy_frame") return "copy_frame";
      if (then.command === "grab_to_ask") return "grab_to_ask";
      if (then.command === "toggle_mappings") return "toggle_mappings";
      if (then.command === "toggle_camera") return "toggle_camera";
      if (then.command === "flash_stage") return "flash_stage";
      return "none";
    case "emit":
      if (then.channel === "visionforge.pause" || then.channel === "visionforge.mute") {
        return "toggle_mappings";
      }
      return "custom";
    case "sequence":
      return "custom";
    case "custom_ref":
      return "custom_ref";
    case "mouse":
      return "none";
    default:
      return "none";
  }
}

export function describeThen(then: ActionThen): string {
  if (then.kind === "sequence") {
    return then.label || `Custom (${then.steps.length} step${then.steps.length === 1 ? "" : "s"})`;
  }
  if (then.kind === "custom_ref") return `Recipe ${then.outputId}`;
  if (then.kind === "open_url") return `Open ${then.url}`;
  if (then.kind === "hotkey") return `Hotkey ${then.keys}`;
  if (then.kind === "forge_handoff") {
    if (then.params?.autogen !== "0") {
      if (then.product === "videoforge") return "Open VideoForge · generate";
      if (then.product === "imageforge") return "Open ImageForge · generate";
    }
    return `Open ${then.product}`;
  }
  if (then.kind === "toast" && then.message) return `Toast: ${then.message}`;
  if (then.kind === "speak" && then.text) return `Speak: ${then.text}`;
  const preset = thenToPreset(then);
  const meta = ACTION_PRESETS.find((p) => p.id === preset);
  return meta?.label || then.kind;
}

export function extractStepFields(step: ActionThen): StepFieldOpts {
  switch (step.kind) {
    case "toast":
      return { text: step.message || "" };
    case "speak":
      return { text: step.text || "" };
    case "copy_text":
      return { text: step.text || "" };
    case "beep":
      return { frequency: step.frequency ?? 880 };
    case "scroll":
      return { dy: Math.abs(step.dy) };
    case "open_url":
      return { url: step.url };
    case "forge_handoff":
      return {
        text: step.params?.prompt || "",
        prompt: step.params?.prompt || "",
        autogen: step.params?.autogen,
        provider: step.params?.provider,
        pick: step.params?.pick,
        model: step.params?.model,
        sku: step.params?.sku,
        rating: step.params?.rating,
      };
    case "hotkey":
      return { keys: step.keys };
    case "emit":
      return { channel: step.channel };
    default:
      return {};
  }
}
